import type { QueryClient } from '@tanstack/vue-query';
import type { BulkChange, BulkOperation } from './useBulkActionApi';
import { LIST_SEARCH_QUERY_KEY, patchCachedListSchedules } from './useScheduleMutations';
import { NESTED_SCHEDULES_FIELD, patchPendingScheduleSaves, recordScheduleDeleted, scheduleItems, toNestedScheduleItem } from './scheduleItems';
import { ROW_END_DATE, ROW_START_DATE, isRecurringRow, scheduleKind, type ScheduleRow } from '@/utils/customPlaylistSchedule';
import { isIndexedSchedule } from '@/components/arrayColumns/scheduleCell';

/**
 * Bring the cached search rows in line with a bulk run that has just finished.
 *
 * The search documents are reindexed by a queued job after the run. Until that job lands, a
 * refetch returns the old documents, and the live poll brings the old values back every 30
 * seconds. So the rows are patched in place, in both the search pages and the live-poll row set.
 * The pattern is the one `useScheduleMutations` uses for a single write.
 *
 * The run's report says which rows changed, but not their new values. The new values are worked
 * out here from the operation the user confirmed: a `set` writes its value, a `clear` writes null,
 * and a `shift_days` moves a date by that many days. A derived field, such as a schedule's
 * effective start, is left alone until the reindexed document arrives.
 */

export const SCHEDULE_ENTITY_SLUG = 'custom_playlist_schedule';
export const LIST_ENTITY_SLUG = 'location_zone_custom_playlist';

/** The list id a schedule's search document carries. */
const SCHEDULE_LIST_ID_FIELD = 'location_zone_custom_playlist_id';

export interface BulkCachePatchInput {
  entity: string;
  operation: BulkOperation;
  /** The record ids whose item finished as `done`. */
  doneRecordIds: readonly number[];
  /**
   * Display labels the user saw when picking values. Keyed by field name for a lookup value, and
   * by `tag:<id>` for a tag. The server never sees them.
   */
  valueLabels?: Record<string, string>;
  /**
   * Each cached schedule as it read when the run started, keyed by schedule id, from
   * `snapshotSchedulesBeforeRun`. When it holds every changed schedule, a shift is patched from
   * these values. Without it, shifted rows are fetched again instead.
   */
  schedulesBeforeRun?: SchedulesBeforeRun;
}

type Row = Record<string, unknown>;

/** Each cached schedule as it read just before a schedule run was executed, keyed by schedule id. */
export type SchedulesBeforeRun = ReadonlyMap<number, Row>;

function isRecord(value: unknown): value is Row {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(.*)$/s;

/**
 * Move a `YYYY-MM-DD` value, or a value that starts with one, by a whole number of days.
 * The arithmetic is done in UTC so a daylight-saving change cannot move the date by one.
 * A value that is empty, not a date, or the legacy zero date is returned unchanged. That matches
 * the server, which reports an empty date as "no value" and leaves it empty.
 */
export function shiftDateValue(value: unknown, days: number): unknown {
  if (typeof value !== 'string') return value;
  const match = DATE_PREFIX.exec(value);
  if (!match) return value;
  const [, year, month, day, rest] = match;
  // A legacy "0000-00-00" date means empty. The server leaves it alone, so the patch does too.
  if (year === '0000') return value;
  const shifted = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + days));
  const yyyy = String(shifted.getUTCFullYear()).padStart(4, '0');
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(shifted.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}${rest}`;
}

/** The nested relation a lookup id belongs to, for example `source_bucket` for `source_bucket_id`. */
function relationKeyFor(field: string): string | null {
  return field.endsWith('_id') ? field.slice(0, -3) : null;
}

/**
 * Apply one change to one row. The row is copied, never mutated.
 *
 * @param shiftFrom The row as it read before the run. A shift moves its value, not the row's, so
 *   a row that already holds the shifted value is not moved twice.
 */
function applyChange(row: Row, change: BulkChange, valueLabels: Record<string, string>, shiftFrom: Row): Row {
  const next: Row = { ...row };
  if (change.op === 'shift_days') {
    // An earlier copy without this field has nothing to shift from. The row keeps its value
    // until the reindexed document arrives, rather than losing it to undefined.
    if (shiftFrom[change.field] === undefined) return next;
    next[change.field] = shiftDateValue(shiftFrom[change.field], Number(change.value));
    return next;
  }

  const value = change.op === 'clear' ? null : change.value;
  next[change.field] = value;

  // A lookup id has a nested `{ id, display_string }` beside it on the search document. Redraw
  // it when the label is known. Otherwise leave it for the reindexed document to correct.
  const relationKey = relationKeyFor(change.field);
  if (relationKey !== null && relationKey in row) {
    if (value === null) {
      next[relationKey] = null;
    } else if (valueLabels[change.field] !== undefined) {
      const existing = isRecord(row[relationKey]) ? (row[relationKey] as Row) : {};
      next[relationKey] = { ...existing, id: value, display_string: valueLabels[change.field] };
    }
  }
  return next;
}

interface TagLike {
  id?: unknown;
  display_string?: unknown;
}

function applyTagOperation(row: Row, operation: BulkOperation, valueLabels: Record<string, string>): Row {
  if (operation.action !== 'add_tag' && operation.action !== 'remove_tag') return row;
  const current = Array.isArray(row.tags) ? (row.tags as TagLike[]) : [];
  const tagIds = new Set(operation.tag_ids);
  if (operation.action === 'remove_tag') {
    return { ...row, tags: current.filter((tag) => !tagIds.has(Number(tag.id))) };
  }
  const present = new Set(current.map((tag) => Number(tag.id)));
  const added = operation.tag_ids
    .filter((tagId) => !present.has(tagId) && valueLabels[`tag:${tagId}`] !== undefined)
    .map((tagId) => ({ id: tagId, display_string: valueLabels[`tag:${tagId}`] }));
  return { ...row, tags: [...current, ...added] };
}

/**
 * The changes that reach this row, on its real columns, as the server resolves them: a change
 * limited to other kinds of schedule is left out, the editor's start and end date land on the
 * recurring pair of a row that repeats and on `date` and `end_date` otherwise, and a message or
 * silence's times also write its messaging window.
 */
export function changesForRow(row: Row, changes: BulkChange[]): BulkChange[] {
  const schedule = row as ScheduleRow;
  const kind = scheduleKind(schedule);
  const rowChanges = changes.filter((change) => !change.kinds || change.kinds.includes(kind));
  // The days after this edit decide the pair, since the players read the row as it is saved.
  const dayChange = [...rowChanges].reverse().find((change) => change.field === 'delimited_day_ids');
  const repeats = isRecurringRow(dayChange ? { ...schedule, delimited_day_ids: dayChange.op === 'set' ? (dayChange.value as string) : null } : schedule);
  const usesMessagingTimes = kind === 'message' || kind === 'silence';
  return rowChanges.flatMap((change) => {
    const field =
      change.field === ROW_START_DATE ? (repeats ? 'recurring_start_date' : 'date')
        : change.field === ROW_END_DATE ? (repeats ? 'recurring_end_date' : 'end_date')
          : change.field;
    const resolved = { ...change, field };
    const messagingField = field === 'time' ? 'messaging_start_time' : field === 'end_time' ? 'messaging_end_time' : null;
    return usesMessagingTimes && messagingField ? [resolved, { ...change, field: messagingField }] : [resolved];
  });
}

/**
 * The row as it reads after the operation.
 *
 * @param shiftFrom The row as it read before the run, when that is known. A shift starts from it.
 */
export function applyBulkOperationToRow(row: Row, operation: BulkOperation, valueLabels: Record<string, string> = {}, shiftFrom: Row = row): Row {
  if (operation.action === 'update') {
    return changesForRow(row, operation.changes).reduce((patched, change) => applyChange(patched, change, valueLabels, shiftFrom), row);
  }
  return applyTagOperation(row, operation, valueLabels);
}

/** Rewrite the rows of every cached search page and every live-poll row set of one entity. */
function mapCachedRows(queryClient: QueryClient, entity: string, mapRows: (rows: unknown[]) => unknown[]): void {
  queryClient.setQueriesData({ queryKey: ['search', entity] }, (page: unknown) => {
    if (!isRecord(page) || !Array.isArray(page.data)) return page;
    const nextRows = mapRows(page.data);
    return nextRows === page.data ? page : { ...page, data: nextRows };
  });
  queryClient.setQueriesData({ queryKey: ['search-live-poll', entity] }, (rows: unknown) =>
    Array.isArray(rows) ? mapRows(rows) : rows,
  );
}

function patchRowsById(queryClient: QueryClient, entity: string, ids: Set<number>, patch: (row: Row) => Row): void {
  mapCachedRows(queryClient, entity, (rows) => {
    if (!rows.some((row) => isRecord(row) && ids.has(Number(row.id)))) return rows;
    return rows.map((row) => (isRecord(row) && ids.has(Number(row.id)) ? patch(row) : row));
  });
}

function dropRows(queryClient: QueryClient, entity: string, shouldDrop: (row: Row) => boolean): void {
  mapCachedRows(queryClient, entity, (rows) => {
    if (!rows.some((row) => isRecord(row) && shouldDrop(row))) return rows;
    return rows.filter((row) => !(isRecord(row) && shouldDrop(row)));
  });
}

/**
 * Visit every cached copy of a schedule with the list it sits on. A schedule is found in two
 * places: as a row of a cached schedule search, which names its list, and as a nested item on a
 * cached custom list search row. The custom list search is where schedules are picked now, so for
 * a run started there the nested items are the only copy.
 */
function forEachCachedSchedule(
  queryClient: QueryClient,
  visit: (listId: number, schedule: Row, dataUpdatedAt: number) => void,
): void {
  const visitScheduleRows = (rows: unknown, dataUpdatedAt: number) => {
    if (!Array.isArray(rows)) return;
    for (const row of rows) {
      if (isRecord(row)) visit(Number(row[SCHEDULE_LIST_ID_FIELD]), row, dataUpdatedAt);
    }
  };
  const visitListRows = (rows: unknown, dataUpdatedAt: number) => {
    if (!Array.isArray(rows)) return;
    for (const row of rows) {
      if (!isRecord(row)) continue;
      for (const item of scheduleItems(row[NESTED_SCHEDULES_FIELD])) visit(Number(row.id), item as Row, dataUpdatedAt);
    }
  };
  const queriesOf = (queryKey: unknown[]) => queryClient.getQueryCache().findAll({ queryKey });
  for (const query of queriesOf(['search', SCHEDULE_ENTITY_SLUG])) {
    const page = query.state.data;
    if (isRecord(page)) visitScheduleRows(page.data, query.state.dataUpdatedAt);
  }
  for (const query of queriesOf(['search-live-poll', SCHEDULE_ENTITY_SLUG])) {
    visitScheduleRows(query.state.data, query.state.dataUpdatedAt);
  }
  for (const query of queriesOf(['search', LIST_ENTITY_SLUG])) {
    const page = query.state.data;
    if (isRecord(page)) visitListRows(page.data, query.state.dataUpdatedAt);
  }
  for (const query of queriesOf(['search-live-poll', LIST_ENTITY_SLUG])) {
    visitListRows(query.state.data, query.state.dataUpdatedAt);
  }
}

/** The list each changed schedule sits on, read before the rows are patched or dropped. */
function cachedScheduleListIds(queryClient: QueryClient, scheduleIds: Set<number>): Map<number, number[]> {
  const byList = new Map<number, Set<number>>();
  forEachCachedSchedule(queryClient, (listId, schedule) => {
    const scheduleId = Number(schedule.id);
    if (!scheduleIds.has(scheduleId) || !Number.isInteger(listId) || listId <= 0) return;
    const ids = byList.get(listId) ?? new Set<number>();
    ids.add(scheduleId);
    byList.set(listId, ids);
  });
  return new Map([...byList].map(([listId, ids]) => [listId, [...ids]]));
}

/**
 * Every cached schedule as it reads now, keyed by id. Taken just before a schedule run is
 * executed, so a shift can later be patched from the values the page held before the run.
 *
 * A schedule can be cached several times, for example on an old search page left inactive and on
 * the page on screen. Each schedule's copy comes from the query fetched most recently. An item
 * still in the old `{id, display_string}` shape is skipped, because it has no dates to shift from.
 */
export function snapshotSchedulesBeforeRun(queryClient: QueryClient): Map<number, Row> {
  const newest = new Map<number, { schedule: Row; dataUpdatedAt: number }>();
  forEachCachedSchedule(queryClient, (_listId, schedule, dataUpdatedAt) => {
    const scheduleId = Number(schedule.id);
    if (!Number.isInteger(scheduleId) || scheduleId <= 0 || !isIndexedSchedule(schedule)) return;
    const held = newest.get(scheduleId);
    if (!held || dataUpdatedAt > held.dataUpdatedAt) newest.set(scheduleId, { schedule, dataUpdatedAt });
  });
  return new Map([...newest].map(([scheduleId, { schedule }]) => [scheduleId, schedule]));
}

/** The field a custom list search row uses to say which of its schedules matched the search. */
const MATCHED_SCHEDULE_IDS_FIELD = 'matched_schedule_ids';

/** Take deleted schedules out of each cached list row's matched schedule ids. */
function dropMatchedScheduleIds(queryClient: QueryClient, scheduleIds: Set<number>): void {
  const holdsDeleted = (row: unknown) =>
    isRecord(row) && Array.isArray(row[MATCHED_SCHEDULE_IDS_FIELD])
    && (row[MATCHED_SCHEDULE_IDS_FIELD] as unknown[]).some((id) => scheduleIds.has(Number(id)));
  mapCachedRows(queryClient, LIST_ENTITY_SLUG, (rows) => {
    if (!rows.some(holdsDeleted)) return rows;
    return rows.map((row) => holdsDeleted(row)
      ? { ...(row as Row), [MATCHED_SCHEDULE_IDS_FIELD]: ((row as Row)[MATCHED_SCHEDULE_IDS_FIELD] as unknown[]).filter((id) => !scheduleIds.has(Number(id))) }
      : row);
  });
}

/**
 * @param runOperation The whole operation the run applied. The browser's own earlier saves were
 *   made before the run, so they take all of it, shifts included.
 */
function patchScheduleCaches(queryClient: QueryClient, input: BulkCachePatchInput, ids: Set<number>, runOperation: BulkOperation): void {
  const { operation } = input;
  const valueLabels = input.valueLabels ?? {};
  const listIds = cachedScheduleListIds(queryClient, ids);
  const patchRow = (row: Row) =>
    applyBulkOperationToRow(row, operation, valueLabels, input.schedulesBeforeRun?.get(Number(row.id)) ?? row);

  if (operation.action === 'delete') {
    dropRows(queryClient, SCHEDULE_ENTITY_SLUG, (row) => ids.has(Number(row.id)));
    dropMatchedScheduleIds(queryClient, ids);
  } else {
    patchRowsById(queryClient, SCHEDULE_ENTITY_SLUG, ids, patchRow);
    // The list sections draw this browser's own earlier saves over the cache. Those take the edit too.
    patchPendingScheduleSaves(ids, (saved) => applyBulkOperationToRow(saved, runOperation, valueLabels));
  }

  // The custom list cards draw their schedules from the list's own search document. Patch the
  // lists that hold a changed schedule, the same way a single schedule write does.
  for (const [listId, scheduleIds] of listIds) {
    const scheduleIdSet = new Set(scheduleIds);
    if (operation.action === 'delete') {
      for (const scheduleId of scheduleIds) recordScheduleDeleted(listId, scheduleId);
      patchCachedListSchedules(queryClient, listId, (items) =>
        scheduleItems(items).filter((item) => !scheduleIdSet.has(Number(item.id))),
      );
    } else {
      patchCachedListSchedules(queryClient, listId, (items) =>
        scheduleItems(items).map((item) =>
          scheduleIdSet.has(Number(item.id))
            ? toNestedScheduleItem(patchRow(item as Row))
            : item,
        ),
      );
    }
  }

  // Lists that were not cached still hold the old nested schedules. Marking them
  // stale without a refetch makes the next page change fetch the reindexed documents.
  void queryClient.invalidateQueries({ queryKey: LIST_SEARCH_QUERY_KEY, refetchType: 'none' });
}

function patchListCaches(queryClient: QueryClient, input: BulkCachePatchInput, ids: Set<number>): void {
  const { operation } = input;
  const valueLabels = input.valueLabels ?? {};

  if (operation.action === 'delete') {
    dropRows(queryClient, LIST_ENTITY_SLUG, (row) => ids.has(Number(row.id)));
    // A list's schedules are deleted with it. Drop them from the schedule search too.
    dropRows(queryClient, SCHEDULE_ENTITY_SLUG, (row) => ids.has(Number(row[SCHEDULE_LIST_ID_FIELD])));
    void queryClient.invalidateQueries({ queryKey: ['search', SCHEDULE_ENTITY_SLUG], refetchType: 'none' });
    return;
  }

  patchRowsById(queryClient, LIST_ENTITY_SLUG, ids, (row) => {
    const patched = applyBulkOperationToRow(row, operation, valueLabels);
    // Keep the nested schedules exactly as they were. A list edit never changes them.
    return NESTED_SCHEDULES_FIELD in row ? { ...patched, [NESTED_SCHEDULES_FIELD]: row[NESTED_SCHEDULES_FIELD] } : patched;
  });
}

/**
 * Patch every cached copy of the rows a finished run changed, then mark the entity's searches
 * stale without refetching them.
 */
export function applyBulkRunToCaches(queryClient: QueryClient, input: BulkCachePatchInput): void {
  const ids = new Set(input.doneRecordIds);
  // A shift is relative, so it is not safe to apply to a cached value twice. A page that fetched
  // while the run was working may already hold the shifted value, because the server reindexes
  // after every batch. So only the set and clear changes are patched, and the searches are
  // fetched again when the run shifted anything.
  // The exception is a schedule run that recorded every changed schedule when it started. A shift
  // from those values lands on the same date however often the page fetched during the run.
  const shiftsFromSnapshot = input.entity === SCHEDULE_ENTITY_SLUG && input.schedulesBeforeRun !== undefined
    && [...ids].every((id) => input.schedulesBeforeRun!.has(id));
  const hasShift = !shiftsFromSnapshot && input.operation.action === 'update' && input.operation.changes.some((change) => change.op === 'shift_days');
  const patchable: BulkCachePatchInput = hasShift && input.operation.action === 'update'
    ? { ...input, operation: { ...input.operation, changes: input.operation.changes.filter((change) => change.op !== 'shift_days') } }
    : input;
  if (ids.size > 0) {
    if (input.entity === SCHEDULE_ENTITY_SLUG) {
      patchScheduleCaches(queryClient, patchable, ids, input.operation);
    } else if (input.entity === LIST_ENTITY_SLUG) {
      patchListCaches(queryClient, patchable, ids);
    }
  }
  if (hasShift) {
    void queryClient.invalidateQueries({ queryKey: ['search', input.entity] });
    void queryClient.invalidateQueries({ queryKey: ['search-live-poll', input.entity] });
    if (input.entity === SCHEDULE_ENTITY_SLUG) void queryClient.invalidateQueries({ queryKey: LIST_SEARCH_QUERY_KEY });
  } else {
    void queryClient.invalidateQueries({ queryKey: ['search', input.entity], refetchType: 'none' });
  }
  // The schedule search draws each schedule with its list's card, from a batched copy of the list.
  // A list run changes those lists, so the copies are fetched again the next time they are shown.
  if (input.entity === LIST_ENTITY_SLUG) {
    void queryClient.invalidateQueries({ queryKey: ['model-record-batched', 'LocationZoneCustomPlaylist'], refetchType: 'none' });
  }
}
